// src/components/character_creation/steps/RaceSelection.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  Chip,
  Stack,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  LinearProgress,
  Tooltip,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Science as ScienceIcon,
  Language as LanguageIcon,
  Group as GroupIcon,
  LocationOn as LocationOnIcon,
  Psychology as PsychologyIcon,
  Favorite as FavoriteIcon,
  Close as CloseIcon,
  Search as SearchIcon
} from '@mui/icons-material';

const RaceSelection = ({
  races = [],
  selectedRace = null,
  onRaceSelect,
  loading = false
}) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedRaceDetails, setSelectedRaceDetails] = useState(null);
  const [filteredRaces, setFilteredRaces] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Инициализация фильтрованных рас
  useEffect(() => {
    setFilteredRaces(races);
  }, [races]);

  // Фильтрация рас по поисковому запросу
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredRaces(races);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = races.filter(race => 
      race.name?.toLowerCase().includes(term) ||
      race.description?.toLowerCase().includes(term) ||
      race.home_system?.toLowerCase().includes(term) ||
      race.environment?.toLowerCase().includes(term)
    );
    
    setFilteredRaces(filtered);
  }, [searchTerm, races]);

  // Обработчик выбора расы
  const handleRaceSelect = (race) => {
    console.log('🎯 Выбрана раса:', race.name);
    if (onRaceSelect) {
      onRaceSelect(race);
    }
  };

  // Открытие деталей расы
  const handleOpenDetails = (race, event) => {
    event.stopPropagation(); // Предотвращаем всплытие события
    console.log('📖 Открытие деталей расы:', race.name);
    setSelectedRaceDetails(race);
    setDetailsOpen(true);
  };

  // Закрытие деталей
  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedRaceDetails(null);
  };

  // Проверка, выбрана ли раса
  const isRaceSelected = (race) => {
    return selectedRace && selectedRace.id === race.id;
  };

  // Получение цвета для технологии
  const getTechLevelColor = (level) => {
    const colors = {
      1: 'error',
      2: 'warning',
      3: 'info',
      4: 'primary',
      5: 'success'
    };
    return colors[level] || 'default';
  };

  // Получение названия уровня технологии
  const getTechLevelLabel = (level) => {
    const labels = {
      1: 'Примитивный',
      2: 'Развивающийся',
      3: 'Промышленный',
      4: 'Пост-индустриальный',
      5: 'Космическая цивилизация'
    };
    return labels[level] || `Уровень ${level}`;
  };

  // Получение иконки для среды обитания
  const getEnvironmentIcon = (env) => {
    const icons = {
      'surface': '🌍',
      'aquatic': '🌊',
      'aerial': '☁️',
      'subterranean': '⛰️',
      'space': '🚀',
      'multienvironment': '🔄'
    };
    return icons[env] || '🌍';
  };

  // Рендер карточки расы
  const renderRaceCard = (race) => {
    const isSelected = isRaceSelected(race);
    
    return (
      <Grid item xs={12} sm={6} md={4} key={race.id}>
        <Card
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            border: isSelected ? 3 : 1,
            borderColor: isSelected ? 'primary.main' : 'divider',
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: isSelected ? 4 : 3,
              borderColor: isSelected ? 'primary.dark' : 'action.active'
            }
          }}
        >
          {/* Индикатор выбора */}
          {isSelected && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 1
              }}
            >
              <Chip
                label="Выбрано"
                color="primary"
                size="small"
                icon={<CheckCircleIcon />}
              />
            </Box>
          )}

          <CardActionArea
            onClick={() => handleRaceSelect(race)}
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch'
            }}
          >
            {/* Заголовок с иконкой */}
            <CardContent sx={{ flexGrow: 1, pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                  {race.name}
                </Typography>
                <Tooltip title="Просмотреть детали">
                  <IconButton
                    size="small"
                    onClick={(e) => handleOpenDetails(race, e)}
                    sx={{ ml: 1 }}
                  >
                    <InfoIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Краткое описание */}
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{
                  mb: 2,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {race.description || race.biology?.description || 'Описание отсутствует'}
              </Typography>

              {/* Теги расы */}
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  icon={<ScienceIcon />}
                  label={getTechLevelLabel(race.technology_level || 1)}
                  color={getTechLevelColor(race.technology_level || 1)}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  icon={<Box sx={{ fontSize: '1rem' }}>{getEnvironmentIcon(race.environment)}</Box>}
                  label={race.environment || 'surface'}
                  size="small"
                  variant="outlined"
                />
              </Stack>
            </CardContent>

            {/* Футер с дополнительной информацией */}
            <Box sx={{ p: 2, pt: 0 }}>
              <Divider sx={{ mb: 1 }} />
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  {race.home_system || 'Неизвестная система'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {race.compatible_templates?.length || 0} шаблонов
                </Typography>
              </Stack>
            </Box>
          </CardActionArea>
        </Card>
      </Grid>
    );
  };

  // Диалог с деталями расы
  const renderRaceDetailsDialog = () => {
    if (!selectedRaceDetails) return null;

    const { 
      name, 
      description, 
      biology, 
      psychology, 
      home_system, 
      environment, 
      technology_level,
      lore,
      compatible_templates = [],
      race_compatibility = [],
      image_url 
    } = selectedRaceDetails;

    return (
      <Dialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <GroupIcon sx={{ mr: 1 }} />
              <Typography variant="h6">
                {name}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseDetails} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          {/* Изображение расы (если есть) */}
          {image_url && (
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <CardMedia
                component="img"
                image={image_url}
                alt={name}
                sx={{
                  maxHeight: 200,
                  objectFit: 'contain',
                  borderRadius: 1
                }}
              />
            </Box>
          )}

          <Grid container spacing={3}>
            {/* Основная информация */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <LocationOnIcon sx={{ mr: 1 }} />
                Основная информация
              </Typography>
              
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <ScienceIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Технологический уровень" 
                    secondary={
                      <Chip 
                        label={getTechLevelLabel(technology_level || 1)}
                        color={getTechLevelColor(technology_level || 1)}
                        size="small"
                      />
                    }
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <Box sx={{ fontSize: '1.5rem' }}>{getEnvironmentIcon(environment)}</Box>
                  </ListItemIcon>
                  <ListItemText 
                    primary="Среда обитания" 
                    secondary={environment || 'surface'} 
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <LocationOnIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Родная система" 
                    secondary={home_system || 'Неизвестна'} 
                  />
                </ListItem>
              </List>
            </Grid>

            {/* Биология и психология */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <PsychologyIcon sx={{ mr: 1 }} />
                Биология и психология
              </Typography>
              
              {biology && (
                <Typography variant="body2" paragraph>
                  <strong>Биология:</strong> {typeof biology === 'object' ? biology.description : biology}
                </Typography>
              )}
              
              {psychology && (
                <Typography variant="body2" paragraph>
                  <strong>Психология:</strong> {typeof psychology === 'object' ? psychology.description : psychology}
                </Typography>
              )}
            </Grid>

            {/* Полное описание */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Полное описание
              </Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2">
                  {description || 'Описание отсутствует'}
                </Typography>
              </Paper>
            </Grid>

            {/* Лор (если есть) */}
            {lore && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  История и лор
                </Typography>
                <Alert severity="info" icon={<FavoriteIcon />}>
                  <Typography variant="body2">
                    {lore}
                  </Typography>
                </Alert>
              </Grid>
            )}

            {/* Совместимые шаблоны */}
            {compatible_templates && compatible_templates.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Совместимые шаблоны
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {compatible_templates.map((template, index) => (
                    <Chip
                      key={index}
                      label={template}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Stack>
              </Grid>
            )}

            {/* Совместимость с другими расами */}
            {race_compatibility && race_compatibility.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Совместимость с другими расами
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {race_compatibility.join(', ')}
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseDetails}>
            Закрыть
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              handleRaceSelect(selectedRaceDetails);
              handleCloseDetails();
            }}
            startIcon={<CheckCircleIcon />}
          >
            Выбрать эту расу
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Отображение загрузки
  if (loading) {
    return (
      <Box sx={{ width: '100%', textAlign: 'center', py: 4 }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Загрузка рас...
        </Typography>
      </Box>
    );
  }

  // Отображение ошибки, если нет рас
  if (races.length === 0) {
    return (
      <Alert severity="warning" sx={{ my: 2 }}>
        <Typography variant="body1">
          Не удалось загрузить данные о расах. Пожалуйста, попробуйте перезагрузить страницу.
        </Typography>
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Выбор расы
      </Typography>
      
      <Typography variant="body1" paragraph color="text.secondary">
        Определите происхождение вашего персонажа. Выберите расу, которая определит его биологические особенности, культуру и возможности.
      </Typography>

      {/* Индикатор выбора */}
      {selectedRace && (
        <Alert 
          severity="success" 
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => handleRaceSelect(null)}
              startIcon={<CancelIcon />}
            >
              Отменить выбор
            </Button>
          }
        >
          <Typography variant="subtitle2">
            Выбрана раса: <strong>{selectedRace.name}</strong>
          </Typography>
        </Alert>
      )}

      {/* Поиск (опционально) */}
      <Paper sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center' }}>
        <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
        <input
          type="text"
          placeholder="Поиск рас по названию, описанию или системе..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            border: 'none',
            outline: 'none',
            flex: 1,
            fontSize: '1rem',
            backgroundColor: 'transparent'
          }}
        />
        {searchTerm && (
          <IconButton size="small" onClick={() => setSearchTerm('')}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Paper>

      {/* Информация о количестве */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Доступно рас: {filteredRaces.length} из {races.length}
        </Typography>
        {searchTerm && (
          <Chip
            label={`Найдено: ${filteredRaces.length}`}
            size="small"
            color="info"
            variant="outlined"
          />
        )}
      </Box>

      {/* Сетка рас */}
      <Grid container spacing={3}>
        {filteredRaces.length > 0 ? (
          filteredRaces.map(race => renderRaceCard(race))
        ) : (
          <Grid item xs={12}>
            <Alert severity="info">
              <Typography variant="body1">
                По запросу "{searchTerm}" рас не найдено. Попробуйте другой поисковый запрос.
              </Typography>
            </Alert>
          </Grid>
        )}
      </Grid>

      {/* Диалог с деталями */}
      {renderRaceDetailsDialog()}

      {/* Подсказки */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Как выбрать расу:</strong>
          <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
            <li>Нажмите на карточку расы, чтобы выбрать её</li>
            <li>Нажмите на иконку информации ⓘ для просмотра деталей</li>
            <li>Раса определяет базовые характеристики и совместимость с шаблонами</li>
            <li>Некоторые навыки могут быть недоступны для определенных рас</li>
          </ul>
        </Typography>
      </Alert>
    </Box>
  );
};

export default RaceSelection;